import React, {
    useState,
    useEffect,
    useContext,
    useRef,
    useCallback,
} from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Checkbox,
    Divider,
    List,
    ListItemButton,
    FormControl,
    Select,
    MenuItem,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material";
import {
    Search as SearchIcon,
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { DataContext } from "../../contexts/DataProvider";
import { fetchAllClients, createClient } from "../../services/clients.service";
import { fetchOrgContacts, dryRunContactImport, executeContactImport, mergeContact } from "../../services/contacts.service";
import { linkContact } from "../../services/relationships.service";
import { getMicrosoftOAuthUrl, checkMicrosoftConnectionStatus } from "../../services/microsoft.service";
import {
    getZohoConnectUrl,
    fetchZohoStatus,
    fetchZohoContacts,
    importAllMondayContacts,
    fetchWorksortedCompanies,
    fetchWorksortedClients,
    fetchAdviserLogicContacts,
    fetchAdviserLogicStatus,
    fetchOutlookContacts,
    fetchIntellifloContacts,
    fetchIntellifloStatus,
    fetchConnectorStatus,
} from "../../services/connectors.service";
import { useAlert } from "../../functions/useAlert";
import { useNavigate } from "react-router-dom";
import { getContactConfig } from "../../utils/contactConfig";
import MapClientsStep, { ClientMappingEntry } from "./MapClientsStep";

interface ImportContactDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    activeView?: string;
    setIsAddClientModalOpen?: (open: boolean) => void;
}

// ── Component ──────────────────────────────────────────────────────────────

const ImportContactDialog: React.FC<ImportContactDialogProps> = ({
    open,
    onClose,
    onSave,
    activeView,
    setIsAddClientModalOpen,
}) => {
    const { login } = useContext(DataContext);
    const [setAlert] = useAlert();
    const navigate = useNavigate();

    // ── Step 1 state ──────────────────────────────────────────────────────
    const [results, setResults] = useState<any[]>([]);
    const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
    const [searchText, setSearchText] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [source, setSource] = useState("Monday.com");
    const [connectorStatus, setConnectorStatus] = useState({ isEnabled: true });
    const [loading, setLoading] = useState(false);
    const [isWorksortedConnected, setIsWorksortedConnected] = useState(true);
    const [isAdviserLogicConnected, setIsAdviserLogicConnected] = useState(false);
    const [isMicrosoftConnected, setIsMicrosoftConnected] = useState(true);
    const [microsoftNeedsReconnect, setMicrosoftNeedsReconnect] = useState(false);
    const [microsoftAdminDisabled, setMicrosoftAdminDisabled] = useState(false);
    const [microsoftM365NotConfigured, setMicrosoftM365NotConfigured] = useState(false);
    const [isZohoConnected, setIsZohoConnected] = useState(true);
    const [isIntellifloConnected, setIsIntellifloConnected] = useState(true);
    const [conflicts, setConflicts] = useState<any[]>([]);
    const [newContacts, setNewContacts] = useState<any[]>([]);
    const [conflictResolutions, setConflictResolutions] = useState<Record<string, 'merge' | 'create'>>({});
    const [showConflictResolution, setShowConflictResolution] = useState(false);

    // ── Wizard / Step 2 state ─────────────────────────────────────────────
    const [wizardStep, setWizardStep] = useState<1 | 2>(1);
    const [clientMappings, setClientMappings] = useState<Record<string, ClientMappingEntry>>({});
    const [allClients, setAllClients] = useState<any[]>([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientsLoaded, setClientsLoaded] = useState(false);

    const currentRequestRef = useRef<any>(null);

    // ── Dialog style — wider/taller in Step 2 ────────────────────────────
    const dialogStyle = {
        "& .MuiDialog-paper": {
            width: wizardStep === 2 ? "680px" : "600px",
            height: wizardStep === 2 ? "auto" : "410px",
            maxWidth: "90vw",
            maxHeight: "90vh",
            borderRadius: "12px",
        },
    };

    // ── Reset ─────────────────────────────────────────────────────────────
    const handleCloseDialog = () => {
        currentRequestRef.current = null;
        setSearchText("");
        setSelectedContactIds([]);
        setResults([]);
        setSource("Monday.com");
        setIsSearching(false);
        setWizardStep(1);
        setClientMappings({});
        setClientsLoaded(false);
        setAllClients([]);
        setMicrosoftM365NotConfigured(false);
        onClose();
    };

    // ── Client loading for Step 2 autocomplete ────────────────────────────
    const loadAllClientsForMapping = useCallback(async () => {
        if (clientsLoaded || loadingClients) return;
        setLoadingClients(true);
        try {
            const resp: any = await fetchAllClients();
            setAllClients(Array.isArray(resp) ? resp : []);
            setClientsLoaded(true);
        } catch {
            // silently fail — user can still type a new client name
        } finally {
            setLoadingClients(false);
        }
    }, [clientsLoaded, loadingClients]);

    // ── Wizard helpers ────────────────────────────────────────────────────
    const updateMapping = (contactId: string, updates: Partial<ClientMappingEntry>) => {
        setClientMappings(prev => ({
            ...prev,
            [contactId]: { ...prev[contactId], ...updates },
        }));
    };

    const handleNext = () => {
        if (selectedContactIds.length === 0) return;
        const selectedContacts = results.filter((c: any) => selectedContactIds.includes(c.id));
        const defaultMappings: Record<string, ClientMappingEntry> = {};
        selectedContacts.forEach((c: any) => {
            defaultMappings[c.id] = {
                mode: "new",
                clientName: c.name,
                entityType: "Individual",
                existingClient: null,
                relationshipType: getContactConfig("Individual").defaultRelationship,
            };
        });
        setClientMappings(defaultMappings);
        setWizardStep(2);
        loadAllClientsForMapping();
    };

    // ── Connector helpers (unchanged) ─────────────────────────────────────
    const handleConnectWorksorted = () => {
        const token = localStorage.getItem("token");
        if (token) {
            const frontendRedirectUrl = encodeURIComponent(window.location.href);
            window.location.href = `/api/worksorted-auth/connect?token=${token}&frontendRedirectUrl=${frontendRedirectUrl}`;
        } else {
            setAlert("Authentication token not found. Please sign in again.", "error");
        }
    };

    const handleConnectMicrosoft = async () => {
        try {
            const response: any = await getMicrosoftOAuthUrl(window.location.href);
            if (response.url) {
                window.location.href = response.url;
            } else {
                setAlert("Failed to get Microsoft authorization URL. Ensure Microsoft 365 is configured in Integrations.", "error");
            }
        } catch (error: any) {
            setAlert(error.message || "Failed to connect Microsoft account.", "error");
        }
    };

    const handleConnectZoho = async () => {
        try {
            const baseUrl = window.location.origin;
            const fixedRedirectUri = `${baseUrl}/home`;
            const response: any = await getZohoConnectUrl(fixedRedirectUri);
            if (response.url) {
                window.location.href = response.url;
            } else {
                setAlert("Failed to get Zoho authorization URL", "error");
            }
        } catch (error: any) {
            setAlert(error.message || "Failed to connect to Zoho", "error");
        }
    };

    const navigateToSettings = () => {
        navigate("/admin", { state: { defaultTabIndex: 2 } });
    };

    // ── External contact fetch (unchanged) ────────────────────────────────
    const fetchExternalClients = async (fetchSource: string) => {
        const requestId = Date.now() + Math.random();
        currentRequestRef.current = requestId;

        setIsSearching(true);
        setResults([]);
        try {
            if (fetchSource === "Monday.com") {
                const response: any = await importAllMondayContacts();
                if (currentRequestRef.current !== requestId) return;
                const rawPayload = Array.isArray(response.clients) ? response.clients : [];
                setResults(rawPayload.map((item) => ({
                    id: item.sourceId, name: item.name, email: item.email,
                    phone: item.phone || "", source: item.source,
                    kyc: item.kyc || {}, admin: item.admin || {},
                })));
            } else if (fetchSource === "WorkSorted") {
                const response: any = await fetchWorksortedCompanies();
                if (currentRequestRef.current !== requestId) return;
                setIsWorksortedConnected(response.isConnected);
                if (response.isConnected && response.success && response.resource?.length > 0) {
                    const companyId = response.resource[0].companyId;
                    const clientsResponse: any = await fetchWorksortedClients(companyId);
                    if (currentRequestRef.current !== requestId) return;
                    if (clientsResponse.success) {
                        setResults((clientsResponse.resource || []).map((client) => ({
                            id: client.clientId, name: client.entityName,
                            email: client.email || "",
                            phone: client.mobile || client.homePhone || client.businessPhone || "",
                            source: "WorkSorted",
                            kyc: { dob: client.DOB, employmentStatus: client.employmentStatus, annualIncome: client.incomePerAnnum },
                            admin: { jobTitle: client.occupation, title: client.title },
                        })));
                    }
                } else {
                    setResults([]);
                }
            } else if (fetchSource === "AdviserLogic") {
                const response: any = await fetchAdviserLogicContacts();
                if (currentRequestRef.current !== requestId) return;
                setIsAdviserLogicConnected(response.status || response.isConnected);
                if (response.success && (response.status || response.isConnected) && response.contacts) {
                    setResults((response.contacts || []).map((contact) => ({
                        id: contact.sourceId, name: contact.name, email: contact.email || "",
                        phone: contact.phone || "", source: "AdviserLogic",
                        kyc: contact.kyc || {}, admin: contact.admin || {},
                    })));
                } else {
                    setResults([]);
                }
            } else if (fetchSource === "Microsoft Outlook") {
                const response: any = await fetchOutlookContacts();
                if (currentRequestRef.current !== requestId) return;
                if (response.needsReconnect) {
                    setMicrosoftNeedsReconnect(true); setIsMicrosoftConnected(false); setResults([]);
                } else if (response.success && response.contacts) {
                    setMicrosoftNeedsReconnect(false); setIsMicrosoftConnected(true);
                    setResults((response.contacts || []).map((contact) => ({
                        id: contact.sourceId, name: contact.name, email: contact.email || "",
                        phone: contact.phone || "", source: "Microsoft Outlook",
                        kyc: contact.kyc || {}, admin: contact.admin || {},
                    })));
                } else {
                    setIsMicrosoftConnected(false); setResults([]);
                }
            } else if (fetchSource === "Zoho") {
                const response: any = await fetchZohoContacts();
                if (currentRequestRef.current !== requestId) return;
                setIsZohoConnected(response.success && response.contacts);
                if (response.success && response.contacts) {
                    setResults((response.contacts || []).map((contact) => ({
                        id: contact.sourceId || contact.id, name: contact.name,
                        email: contact.email || "", phone: contact.phone || "",
                        source: "Zoho", kyc: contact.kyc || {}, admin: contact.admin || {},
                    })));
                } else {
                    setResults([]);
                }
            } else if (fetchSource === "Intelliflo") {
                const response: any = await fetchIntellifloContacts();
                if (currentRequestRef.current !== requestId) return;
                const isArrayData = Array.isArray(response);
                setIsIntellifloConnected(isArrayData);
                if (isArrayData) {
                    setResults(response.map((contact) => ({
                        id: contact.sourceId, name: contact.name, email: contact.email || "",
                        phone: contact.phone || "", source: "Intelliflo",
                        kyc: contact.kyc || {}, admin: contact.admin || {},
                    })));
                } else {
                    setResults([]);
                }
            }
        } catch {
            if (currentRequestRef.current === requestId) setResults([]);
        } finally {
            if (currentRequestRef.current === requestId) setIsSearching(false);
        }
    };

    const checkAndFetch = async (sourceName: string) => {
        if (!login.data.orgId) return;
        const requestId = Date.now() + Math.random();
        currentRequestRef.current = requestId;
        setResults([]);
        setIsSearching(true);
        setConnectorStatus({ isEnabled: true });
        try {
            if (sourceName === "AdviserLogic") {
                const statusRes: any = await fetchAdviserLogicStatus();
                if (currentRequestRef.current !== requestId) return;
                if (statusRes.success && (statusRes.status || statusRes.isConnected)) {
                    setConnectorStatus({ isEnabled: true });
                    setIsAdviserLogicConnected(true);
                    await fetchExternalClients(sourceName);
                } else {
                    setConnectorStatus({ isEnabled: true });
                    setIsAdviserLogicConnected(false);
                    setIsSearching(false);
                }
            } else if (sourceName === "Microsoft Outlook") {
                const statusRes: any = await checkMicrosoftConnectionStatus();
                if (currentRequestRef.current !== requestId) return;
                if (statusRes.m365NotConfigured) {
                    setMicrosoftM365NotConfigured(true); setMicrosoftAdminDisabled(false);
                    setIsMicrosoftConnected(false); setConnectorStatus({ isEnabled: true }); setIsSearching(false);
                } else if (statusRes.adminDisabled) {
                    setMicrosoftM365NotConfigured(false); setMicrosoftAdminDisabled(true); setIsMicrosoftConnected(false);
                    setConnectorStatus({ isEnabled: true }); setIsSearching(false);
                } else if (statusRes.needsReconnect) {
                    setMicrosoftM365NotConfigured(false); setMicrosoftAdminDisabled(false); setMicrosoftNeedsReconnect(true);
                    setIsMicrosoftConnected(false); setConnectorStatus({ isEnabled: true }); setIsSearching(false);
                } else if (statusRes.isConnected && statusRes.hasContactsPermission) {
                    setMicrosoftM365NotConfigured(false); setMicrosoftAdminDisabled(false); setMicrosoftNeedsReconnect(false);
                    setIsMicrosoftConnected(true); setConnectorStatus({ isEnabled: true });
                    await fetchExternalClients(sourceName);
                } else {
                    setMicrosoftM365NotConfigured(false); setMicrosoftAdminDisabled(false); setMicrosoftNeedsReconnect(false);
                    setIsMicrosoftConnected(false); setConnectorStatus({ isEnabled: true }); setIsSearching(false);
                }
            } else if (sourceName === "Zoho") {
                const statusRes: any = await fetchZohoStatus();
                if (currentRequestRef.current !== requestId) return;
                if (statusRes.success && statusRes.isConnected) {
                    setConnectorStatus({ isEnabled: true }); setIsZohoConnected(true);
                    await fetchExternalClients(sourceName);
                } else {
                    setConnectorStatus({ isEnabled: true }); setIsZohoConnected(false); setIsSearching(false);
                }
            } else if (sourceName === "Intelliflo") {
                const statusRes: any = await fetchIntellifloStatus();
                if (currentRequestRef.current !== requestId) return;
                if (statusRes.isConnected) {
                    setConnectorStatus({ isEnabled: true }); setIsIntellifloConnected(true);
                    await fetchExternalClients(sourceName);
                } else {
                    setConnectorStatus({ isEnabled: true }); setIsIntellifloConnected(false); setIsSearching(false);
                }
            } else {
                const statusRes: any = await fetchConnectorStatus(login.data.orgId, sourceName);
                if (currentRequestRef.current !== requestId) return;
                if (statusRes.status === true) {
                    setConnectorStatus({ isEnabled: true });
                    await fetchExternalClients(sourceName);
                } else {
                    setConnectorStatus({ isEnabled: false }); setIsSearching(false);
                }
            }
        } catch {
            if (currentRequestRef.current === requestId) {
                setConnectorStatus({ isEnabled: false }); setIsSearching(false);
            }
        }
    };

    // ── Submit handlers ───────────────────────────────────────────────────
    const handleSubmit = async () => {
        const contactsToImport = selectedContactIds.length > 0
            ? results.filter((contact: any) => selectedContactIds.includes(contact.id))
            : [];
        if (contactsToImport.length === 0) return;

        setLoading(true);
        try {
            const contactsPayload = contactsToImport.map((contact: any) => ({
                name: contact.name, email: contact.email, phone: contact.phone,
                source: contact.source, sourceId: contact.id,
                kyc: contact.kyc, admin: contact.admin,
            }));

            const dryRunResponse: any = await dryRunContactImport(contactsPayload);
            if (dryRunResponse.error) throw new Error(dryRunResponse.error);

            const { newContacts: safeContacts, conflicts: detectedConflicts } = dryRunResponse.data;

            if (detectedConflicts && detectedConflicts.length > 0) {
                setNewContacts(safeContacts || []);
                setConflicts(detectedConflicts);
                const defaultResolutions: Record<string, 'merge' | 'create'> = {};
                detectedConflicts.forEach((conflict: any, index: number) => {
                    defaultResolutions[conflict.incoming.sourceId || index] = 'merge';
                });
                setConflictResolutions(defaultResolutions);
                setShowConflictResolution(true);
                setLoading(false);
                return;
            }

            await executeImport(contactsPayload);
        } catch (error: any) {
            setAlert(error.message || "An error occurred while checking for duplicates.", "error");
            setLoading(false);
        }
    };

    const handleResolveConflicts = async () => {
        setLoading(true);
        try {
            const contactsPayload: any[] = [...newContacts];
            for (const conflict of conflicts) {
                const resolution = conflictResolutions[conflict.incoming.sourceId || conflict.incoming.email];
                if (resolution === 'merge') {
                    contactsPayload.push({ ...conflict.incoming, _mergeWith: conflict.existing._id });
                } else {
                    contactsPayload.push({ ...conflict.incoming, force: true });
                }
            }
            await executeImport(contactsPayload);
        } catch (error: any) {
            setAlert(error.message || "An error occurred while importing contacts.", "error");
            setLoading(false);
        }
    };

    const executeImport = async (contactsPayload: any[]) => {
        try {
            const payloadContacts = contactsPayload.map(contact => ({
                name: contact.name, email: contact.email, phone: contact.phone,
                source: contact.source, sourceId: contact.sourceId,
                orgId: login.data.orgId, userId: login.data.id,
                kyc: contact.kyc, admin: contact.admin,
                force: contact.force || false,
                _mergeWith: contact._mergeWith || undefined,
            }));

            const mergePromises: Promise<any>[] = [];
            const importContacts = payloadContacts.filter(contact => {
                if (contact._mergeWith) {
                    mergePromises.push(
                        mergeContact(contact._mergeWith, { name: contact.name, email: contact.email, phone: contact.phone, kyc: contact.kyc, admin: contact.admin })
                    );
                    return false;
                }
                return true;
            });

            const mergeResults = await Promise.all(mergePromises);
            let importedCount = 0;
            if (importContacts.length > 0) {
                const importResponse: any = await executeContactImport(importContacts);
                if (importResponse.error) throw new Error(importResponse.error);
                importedCount = importResponse.data.imported || importContacts.length;
            }

            const mergeCount = mergeResults.length;
            let message = `${importedCount + mergeCount} contact(s) processed successfully!`;
            if (mergeCount > 0) message += ` ${mergeCount} merged, ${importedCount} created.`;

            // ── Client mapping: create clients + link relationships ────────────
            const mappingsToProcess = Object.entries(clientMappings).filter(
                ([, m]) => m.mode !== "skip"
            );
            let linkSuccessCount = 0;
            let linkFailCount = 0;

            if (mappingsToProcess.length > 0) {
                try {
                    const contactsListResp: any = await fetchOrgContacts(login.data.orgId);
                    const allContactsList: any[] = contactsListResp.contacts || [];

                    const sourceIdToDbId: Record<string, string> = {};
                    const emailToDbId: Record<string, string> = {};
                    allContactsList.forEach((c: any) => {
                        if (c.sourceId) sourceIdToDbId[String(c.sourceId)] = c._id;
                        if (c.email) emailToDbId[c.email.toLowerCase()] = c._id;
                    });
                    // Merged contacts: use their existing DB ID directly
                    for (const conflict of conflicts) {
                        const key = conflict.incoming.sourceId ? String(conflict.incoming.sourceId) : "";
                        if (key && conflict.existing?._id) sourceIdToDbId[key] = conflict.existing._id;
                    }

                    for (const [sourceId, mapping] of mappingsToProcess) {
                        const selectedContact = results.find((c: any) => String(c.id) === sourceId);
                        if (!selectedContact) continue;

                        const dbContactId =
                            sourceIdToDbId[sourceId] ||
                            (selectedContact.email ? emailToDbId[selectedContact.email.toLowerCase()] : null);
                        if (!dbContactId) { linkFailCount++; continue; }

                        try {
                            let linkedClientId: string | null = null;
                            if (mapping.mode === "new") {
                                const clientResp: any = await createClient({
                                    name: mapping.clientName.trim() || selectedContact.name,
                                    entityType: mapping.entityType,
                                    orgId: login.data.orgId,
                                    userId: login.data.id,
                                    createdBy: login.data.id,
                                    profile: { clientStatus: "Active" },
                                });
                                if (!clientResp.error) linkedClientId = clientResp._id || clientResp.id;
                            } else if (mapping.mode === "existing" && mapping.existingClient) {
                                linkedClientId = mapping.existingClient._id || mapping.existingClient.id;
                            }

                            if (linkedClientId) {
                                await linkContact({
                                    clientId: linkedClientId,
                                    contactId: dbContactId,
                                    relationshipType: mapping.relationshipType,
                                    isPrimary: false,
                                    orgId: login.data.orgId,
                                    userId: login.data.id,
                                });
                                linkSuccessCount++;
                            } else {
                                linkFailCount++;
                            }
                        } catch {
                            linkFailCount++;
                        }
                    }

                    if (linkSuccessCount > 0) message += ` ${linkSuccessCount} contact(s) linked to clients.`;
                    if (linkFailCount > 0) message += ` ${linkFailCount} client link(s) failed — link manually from the contact profile.`;
                } catch {
                    message += ` Client linking failed — link manually from the contact profile.`;
                }
            }
            // ─────────────────────────────────────────────────────────────────

            setAlert(message, linkFailCount > 0 ? "warning" : "success");
            onSave();
            handleCloseDialog();
        } catch (error: any) {
            setAlert(error.message || "An error occurred while importing contacts.", "error");
        } finally {
            setLoading(false);
            setShowConflictResolution(false);
            setConflicts([]);
            setNewContacts([]);
            setConflictResolutions({});
        }
    };

    // ── Effects ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (open) checkAndFetch("Monday.com");
    }, [open]);

    const filteredResults = results.filter(
        (c: any) =>
            c.name.toLowerCase().includes(searchText.toLowerCase()) ||
            (c.email && c.email.toLowerCase().includes(searchText.toLowerCase()))
    );

    const handleSelectAllToggle = () => {
        if (selectedContactIds.length === filteredResults.length) {
            setSelectedContactIds([]);
        } else {
            setSelectedContactIds(filteredResults.map((c: any) => c.id));
        }
    };

    const handleContactToggle = (contactId: string) => {
        setSelectedContactIds(prev =>
            prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
        );
    };

    // ── Derived ───────────────────────────────────────────────────────────
    const selectedContacts = results.filter((c: any) => selectedContactIds.includes(c.id));

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onClose={handleCloseDialog} sx={dialogStyle}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                    {showConflictResolution ? "Resolve Duplicates" : "Import Contacts"}
                </Box>
                {!showConflictResolution && (
                    <Stepper activeStep={wizardStep - 1} sx={{ minWidth: 240 }}>
                        <Step><StepLabel>Select</StepLabel></Step>
                        <Step><StepLabel>Map Clients</StepLabel></Step>
                    </Stepper>
                )}
                {wizardStep === 1 && activeView === "clients" && setIsAddClientModalOpen && (
                    <Box
                        sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                        onClick={() => { handleCloseDialog(); setIsAddClientModalOpen(true); }}
                    >
                        <Typography variant="body1" sx={{ color: "primary.main", mr: 1 }}>Add Manually</Typography>
                        <AddIcon sx={{ color: "primary.main" }} />
                    </Box>
                )}
            </DialogTitle>

            {/* ── STEP 1: Contact selection ─────────────────────────────────── */}
            {!showConflictResolution && wizardStep === 1 && (
                <>
                    <DialogContent sx={{overflow: "hidden"}}>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Box display="flex" alignItems="center" border="1px solid #ccc" borderRadius={1} px={2} py={0.5}>
                                <SearchIcon sx={{ color: "gray", mr: 1 }} />
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Search by name or email..."
                                    style={{ border: "none", outline: "none", flex: 1, fontSize: "16px", background: "transparent", height: "40px" }}
                                />
                                <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />
                                <FormControl variant="standard" sx={{ minWidth: 120 }}>
                                    <Select
                                        disableUnderline
                                        value={source}
                                        onChange={(e) => {
                                            const newSource = e.target.value;
                                            setSource(newSource);
                                            setSearchText("");
                                            setSelectedContactIds([]);
                                            checkAndFetch(newSource);
                                        }}
                                    >
                                        <MenuItem value="Monday.com">Monday.com</MenuItem>
                                        <MenuItem value="WorkSorted">WorkSorted</MenuItem>
                                        <MenuItem value="AdviserLogic">AdviserLogic</MenuItem>
                                        <MenuItem value="Microsoft Outlook">Microsoft Account</MenuItem>
                                        <MenuItem value="Zoho">Zoho CRM</MenuItem>
                                        <MenuItem value="Intelliflo">Intelliflo</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {login?.data?.role === "user" ? (
                                <Paper variant="outlined" sx={{ height: "160px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>No import options available</Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">Please contact your organization admin to enable connectors.</Typography>
                                </Paper>
                            ) : source === "AdviserLogic" && !isAdviserLogicConnected ? (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 2, height: "160px" }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>AdviserLogic Not Connected</Typography>
                                    <Typography variant="body2" sx={{ mb: 2, textAlign: "center" }}>Please connect your AdviserLogic account in your Profile under the Integrations tab.</Typography>
                                    <Button variant="contained" onClick={() => { handleCloseDialog(); navigate("/profile?tab=integrations"); }}>Go to Profile Integrations</Button>
                                </Box>
                            ) : !connectorStatus.isEnabled && source !== "AdviserLogic" && source !== "Microsoft Outlook" ? (
                                <Paper variant="outlined" sx={{ height: "160px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>{source} Connector Disabled</Typography>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>Please enable the {source} connector in your organization settings to import contacts.</Typography>
                                    <Button variant="contained" onClick={navigateToSettings}>Enable Connector</Button>
                                </Paper>
                            ) : source === "WorkSorted" && !isWorksortedConnected ? (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 2, height: "160px" }}>
                                    <Typography variant="body2" sx={{ mb: 2 }}>Please connect your Worksorted account.</Typography>
                                    <Button variant="contained" onClick={handleConnectWorksorted}>Connect to Worksorted</Button>
                                </Box>
                            ) : source === "Microsoft Outlook" && !isMicrosoftConnected ? (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 2, height: "160px", gap: 1 }}>
                                    {microsoftM365NotConfigured ? (
                                        <>
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 340 }}>
                                                Microsoft 365 is not configured for your organisation. An admin needs to connect it before you can import Outlook contacts.
                                            </Typography>
                                            <Button variant="contained" onClick={() => { handleCloseDialog(); navigate("/admin", { state: { defaultTabIndex: 2 } }); }}>
                                                Go to Connectors
                                            </Button>
                                        </>
                                    ) : microsoftAdminDisabled ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 320 }}>
                                            Microsoft Outlook has been disabled by your organisation admin.
                                        </Typography>
                                    ) : microsoftNeedsReconnect ? (
                                        <>
                                            <Typography variant="body2" color="warning.main" sx={{ mb: 1, textAlign: "center", maxWidth: 320 }}>
                                                Your Microsoft account connection needs to be updated. Please reconnect your account.
                                            </Typography>
                                            <Button variant="contained" color="warning" onClick={handleConnectMicrosoft}>Reconnect Microsoft Account</Button>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Connect your Microsoft account to import contacts and Teams recordings.</Typography>
                                            <Button variant="contained" onClick={handleConnectMicrosoft}>Connect Microsoft Account</Button>
                                        </>
                                    )}
                                </Box>
                            ) : source === "Zoho" && !isZohoConnected ? (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 2, height: "160px" }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>Zoho CRM Not Connected</Typography>
                                    <Typography variant="body2" sx={{ mb: 2, textAlign: "center" }}>Your organization's Zoho CRM is not connected. Please ask an admin to connect it.</Typography>
                                    <Button variant="contained" onClick={handleConnectZoho}>Connect Zoho CRM (Admin Only)</Button>
                                </Box>
                            ) : source === "Intelliflo" && !isIntellifloConnected ? (
                                <Paper variant="outlined" sx={{ height: "160px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>Intelliflo Connector Disabled</Typography>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>Please enable the Intelliflo connector in your organization settings.</Typography>
                                    <Button variant="contained" onClick={navigateToSettings}>Enable Connector</Button>
                                </Paper>
                            ) : (
                                <Paper variant="outlined" sx={{ height: "193px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
                                    {isSearching ? (
                                        <Box sx={{ m: "auto", textAlign: "center" }}>
                                            <CircularProgress />
                                            <Typography sx={{ mt: 1 }}>Searching contacts...</Typography>
                                        </Box>
                                    ) : filteredResults.length > 0 ? (
                                        <>
                                            <Box sx={{ display: "flex", alignItems: "center", p: 1, borderBottom: 1, borderColor: "divider", bgcolor: "background.default", position: "sticky", top: 0, zIndex: 10 }}>
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedContactIds.length === filteredResults.length && filteredResults.length > 0}
                                                    indeterminate={selectedContactIds.length > 0 && selectedContactIds.length < filteredResults.length}
                                                    onChange={(e) => { e.stopPropagation(); handleSelectAllToggle(); }}
                                                    sx={{ zIndex: 11 }}
                                                />
                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                    {selectedContactIds.length > 0
                                                        ? `${selectedContactIds.length} of ${filteredResults.length} selected`
                                                        : `Select all (${filteredResults.length})`}
                                                </Typography>
                                            </Box>
                                            <List dense sx={{ p: 0 }}>
                                                {filteredResults.map((c: any, i: number) => (
                                                    <React.Fragment key={c.id || i}>
                                                        <ListItemButton onClick={() => handleContactToggle(c.id)} selected={selectedContactIds.includes(c.id)} sx={{ pl: 1 }}>
                                                            <Checkbox size="small" checked={selectedContactIds.includes(c.id)} sx={{ mr: 1 }} />
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center", py: 1, px: 1 }}>
                                                                <Typography variant="body1">{c.name}</Typography>
                                                                <Typography variant="body2" color="text.secondary">{c.email || "No email"}</Typography>
                                                            </Box>
                                                        </ListItemButton>
                                                        {i < filteredResults.length - 1 && <Divider />}
                                                    </React.Fragment>
                                                ))}
                                            </List>
                                        </>
                                    ) : (
                                        <Box sx={{ m: "auto", textAlign: "center" }}>
                                            <Typography color="text.secondary">No matching contacts found.</Typography>
                                        </Box>
                                    )}
                                </Paper>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ pb: 1.7, px: 3 }}>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button
                            onClick={handleNext}
                            variant="contained"
                            disabled={selectedContactIds.length === 0}
                        >
                            {selectedContactIds.length > 0
                                ? `Next: Map Clients (${selectedContactIds.length})`
                                : "Select Contact(s)"}
                        </Button>
                    </DialogActions>
                </>
            )}

            {/* ── STEP 2: Client mapping ─────────────────────────────────────── */}
            {!showConflictResolution && wizardStep === 2 && (
                <>
                    <DialogContent sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                        <MapClientsStep
                            contacts={selectedContacts.map((c: any) => ({
                                id: c.id,
                                name: c.name,
                                email: c.email,
                                phone: c.phone,
                                source: c.source,
                            }))}
                            clientMappings={clientMappings}
                            updateMapping={updateMapping}
                            allClients={allClients}
                            loadingClients={loadingClients}
                            onRequestLoadClients={loadAllClientsForMapping}
                        />
                    </DialogContent>
                    <DialogActions sx={{ pb: 1.7, px: 3 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => setWizardStep(1)}
                            variant="outlined"
                            color="inherit"
                            disabled={loading}
                        >
                            Back
                        </Button>
                        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                            {loading
                                ? <CircularProgress size={24} color="inherit" />
                                : `Import ${selectedContactIds.length} Contact(s)`}
                        </Button>
                    </DialogActions>
                </>
            )}

            {/* ── Conflict resolution (unchanged) ────────────────────────────── */}
            {showConflictResolution && (
                <>
                    <DialogContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>Resolve Duplicates</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            We found {conflicts.length} contact(s) that may already exist. Choose how to handle each:
                        </Typography>
                        <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
                            {conflicts.map((conflict: any, index: number) => {
                                const conflictKey = conflict.incoming.sourceId || conflict.incoming.email || index;
                                return (
                                    <Paper key={conflictKey} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" fontWeight="bold">Incoming: {conflict.incoming.name}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {conflict.incoming.email} {conflict.incoming.phone && `• ${conflict.incoming.phone}`}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 1, color: "warning.main" }}>
                                                    Matches existing: {conflict.existing.name} ({conflict.existing.email})
                                                </Typography>
                                            </Box>
                                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                                <Select
                                                    value={conflictResolutions[conflictKey] || 'merge'}
                                                    onChange={(e) => setConflictResolutions(prev => ({
                                                        ...prev,
                                                        [conflictKey]: e.target.value as 'merge' | 'create',
                                                    }))}
                                                >
                                                    <MenuItem value="merge">Merge</MenuItem>
                                                    <MenuItem value="create">Create as New</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                        {newContacts.length > 0 && (
                            <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                                {newContacts.length} contact(s) will be imported without conflicts.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ pb: 1.7, px: 3 }}>
                        <Button onClick={() => { setShowConflictResolution(false); setConflicts([]); setNewContacts([]); setConflictResolutions({}); }}>
                            Cancel
                        </Button>
                        <Button onClick={handleResolveConflicts} variant="contained" disabled={loading}>
                            {loading
                                ? <CircularProgress size={24} color="inherit" />
                                : `Import ${newContacts.length + conflicts.length} Contact(s)`}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default ImportContactDialog;
